import { io } from "socket.io-client";
import { BackendURL } from "./BackendURL";
import { getAccessToken } from "src/Funcs/auth/authStore";

let socket = null;

/**
 * Get or create the User chat socket (namespace /Users).
 * Reuses the same instance so we never create multiple connections.
 * Authenticates with the current JWT access token; call only when user is logged in.
 * @returns {import("socket.io-client").Socket | null}
 */
export function getSocketUserChat() {
  if (typeof window === "undefined") return null;
  if (!getAccessToken()) return null;

  if (socket) return socket;

  const base = BackendURL.replace(/\/$/, "");
  const socketUrl = `${base}/Users`;
  socket = io(socketUrl, {
    auth: (callback) => callback({ token: getAccessToken() || "" }),
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
  });

  socket.on("connect", () => {
    console.log(
      "[socketUserChat] connected",
      socket.id,
      socket.io.engine.transport?.name,
    );
  });
  socket.on("disconnect", (reason) => {
    console.log(
      "[socketUserChat] disconnected:",
      reason,
      "| transport:",
      socket.io.engine.transport?.name,
    );
  });
  socket.on("connect_error", (err) => {
    console.error("[socketUserChat] connect_error:", err.message);
  });

  socket.io.on("reconnect_attempt", (attempt) => {
    console.log("[socketUserChat] reconnect attempt #" + attempt);
  });
  socket.io.on("reconnect", () => {
    console.log("[socketUserChat] reconnected");
  });
  socket.io.on("reconnect_error", (err) => {
    console.error("[socketUserChat] reconnect_error:", err.message);
  });
  socket.io.on("reconnect_failed", () => {
    console.error("[socketUserChat] reconnect_failed — all attempts exhausted");
  });

  socket.io.engine.on("close", (reason) => {
    console.log("[socketUserChat] engine closed:", reason);
  });

  return socket;
}

/**
 * Disconnect and clear the socket (e.g. on logout).
 */
export function disconnectSocketUserChat() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Wait for socket to be connected (with timeout). Use before emit when socket may still be connecting.
 * @param {import("socket.io-client").Socket} s
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
function waitForConnect(s, timeoutMs = 8000) {
  if (s.connected) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error("Socket connection timeout")),
      timeoutMs,
    );
    s.once("connect", () => {
      clearTimeout(t);
      resolve();
    });
    s.once("connect_error", (err) => {
      clearTimeout(t);
      reject(err);
    });
  });
}

/**
 * Emit a fire-and-forget event once the socket is connected.
 * Use for joinRoom/leaveRoom so the other side reliably joins the room and receives real-time messages.
 * @param {string} event
 * @param {object} data
 * @returns {Promise<void>}
 */
export function emitWhenConnected(event, data = {}) {
  const s = getSocketUserChat();
  if (!s) return Promise.resolve();
  if (s.connected) {
    s.emit(event, data);
    return Promise.resolve();
  }
  return waitForConnect(s).then(() => {
    s.emit(event, data);
  });
}

/**
 * Emit with callback (ack) - returns a Promise that resolves with the server payload.
 * Waits for socket to be connected before emitting so early calls after page load succeed.
 * @param {string} event
 * @param {object} data
 * @param {object} [options]
 * @param {boolean} [options.retryOnTimeout=false] - On ack timeout, force a fresh
 *   connection and emit the event once more (for critical events like sending a message).
 * @returns {Promise<object>}
 */
export function emitWithCallback(event, data = {}, options = {}) {
  const { retryOnTimeout = false } = options;
  const s = getSocketUserChat();
  if (!s) return Promise.reject(new Error("Socket not available"));
  return waitForConnect(s)
    .catch((err) =>
      Promise.reject(
        new Error(`Socket connection timeout (${event}) — ${err?.message ?? "not connected"}`),
      ),
    )
    .then(
      () =>
        new Promise((resolve, reject) => {
          let settled = false;
          const run = (isRetry) => {
            const timeout = setTimeout(
              async () => {
                if (settled) return;
                if (retryOnTimeout && !isRetry) {
                  // The connection may have been half-open. Force a fresh
                  // handshake, wait for it, then emit once more.
                  try {
                    s.disconnect();
                    s.connect();
                  } catch (e) {
                    console.log("[socketUserChat] forced reconnect failed:", e);
                  }
                  try {
                    await waitForConnect(s, 10000);
                  } catch (e) {
                    console.log("[socketUserChat] reconnect wait failed:", e);
                  }
                  if (!settled) run(true);
                  return;
                }
                settled = true;
                try {
                  s.disconnect();
                  s.connect();
                } catch (e) {
                  console.log("[socketUserChat] forced reconnect failed:", e);
                }
                reject(
                  new Error(
                    `Socket timeout: no ack for "${event}" within 15s`,
                  ),
                );
              },
              15000,
            );
            s.emit(event, data, (payload) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              if (payload?.status === false) {
                reject(
                  new Error(
                    `${event} rejected: ${payload.message || "Request failed"}`,
                  ),
                );
              } else {
                resolve(payload);
              }
            });
          };
          run(false);
        }),
    );
}
